import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isNil } from 'lodash';
import { firstValueFrom } from 'rxjs';
import { skip } from 'rxjs/operators';

import { ConversationConfig, ConversationConfigSchema } from '@pwp-common';

import { ConversationConfigService } from '../../../services/conversation/conversation-config/conversation-config.service';
import { makeAuditEntry } from '../../generic/settings/common/audit-entry/helpers/make-audit-entry';
import { AuditEntry } from '../../generic/settings/common/audit-entry/interfaces';

import { dbToSpamDetectionConfigEditorOutput } from './editor-output/db-to-spam-detection-config-editor-output/db-to-spam-detection-config-editor-output';
import { formToSpamDetectionConfigEditorOutput } from './editor-output/form-to-spam-detection-config-editor-output/form-to-spam-detection-config-editor-output';
import { spamDetectionConfigEditorOutputToDb } from './editor-output/spam-detection-config-editor-output-to-db/spam-detection-config-editor-output-to-db';

@UntilDestroy()
@Component({
  selector: 'app-conversation-spam-detection-config-editor',
  templateUrl: './conversation-spam-detection-config-editor.component.html',
  styleUrls: ['./conversation-spam-detection-config-editor.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationSpamDetectionConfigEditorComponent implements OnInit {
  ////////////////////////////////////////////////////////////////////////////
  // Variables
  ////////////////////////////////////////////////////////////////////////////

  private conversationConfig: ConversationConfig | undefined;

  public loading = true;

  public auditEntry: AuditEntry | undefined;

  public waitingRoomEnabled = false;

  public readonly form = new UntypedFormGroup({
    enabled: new UntypedFormControl(false),
    instructions: new UntypedFormControl(''),
    spamDetectedMessage: new UntypedFormControl(''),
    spamFilteringPassedMessage: new UntypedFormControl(''),
  });

  ////////////////////////////////////////////////////////////////////////////
  // Constructor
  ////////////////////////////////////////////////////////////////////////////

  constructor(
    private conversationConfigService: ConversationConfigService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  ////////////////////////////////////////////////////////////////////////////

  public ngOnInit(): void {
    this.enabled.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.onEnabledChange());

    this.refreshData().catch((err) =>
      console.error('ConversationSpamDetectionConfigEditorComponent: failed to refresh data', err),
    );

    this.conversationConfigService
      .getUniqueDoc({ query: [], takeOne: false })
      .pipe(skip(1), untilDestroyed(this))
      .subscribe((conversationConfig) => {
        if (isNil(conversationConfig)) {
          return;
        }
        this.setWaitingRoomEnabled(conversationConfig.getWaitingRoomConfig()?.getEnabled() ?? false);
        this.changeDetectorRef.detectChanges();
      });
  }

  ////////////////////////////////////////////////////////////////////////////
  // Update UI
  ////////////////////////////////////////////////////////////////////////////

  public updateUI = (): void => {
    if (isNil(this.conversationConfig)) {
      return;
    }

    this.loading = true;
    const editorOutput = dbToSpamDetectionConfigEditorOutput(this.conversationConfig.getSpamDetectionConfig());

    this.form.patchValue(
      {
        enabled: editorOutput.enabled,
        instructions: editorOutput.instructions,
        spamDetectedMessage: editorOutput.spamDetectedMessage,
        spamFilteringPassedMessage: editorOutput.spamFilteringPassedMessage,
      },
      { emitEvent: false },
    );

    this.setWaitingRoomEnabled(this.conversationConfig.getWaitingRoomConfig()?.getEnabled() ?? false);

    this.onEnabledChange();
    this.auditEntry = makeAuditEntry(this.conversationConfig);
    this.loading = false;
    this.changeDetectorRef.detectChanges();
  };

  ////////////////////////////////////////////////////////////////////////////
  // Get Data
  ////////////////////////////////////////////////////////////////////////////

  public refreshData = async (): Promise<void> => {
    this.loading = true;
    this.conversationConfig = await firstValueFrom(this.conversationConfigService.getUniqueDoc({ query: [] }));
    this.updateUI();
  };

  ////////////////////////////////////////////////////////////////////////////
  // Upload
  ////////////////////////////////////////////////////////////////////////////

  public upload = async (): Promise<void> => {
    if (isNil(this.conversationConfig)) {
      return;
    }

    const editorOutput = formToSpamDetectionConfigEditorOutput(this.form.value);

    if (editorOutput.enabled) {
      this.form.patchValue(
        {
          instructions: editorOutput.instructions,
          spamDetectedMessage: editorOutput.spamDetectedMessage,
          spamFilteringPassedMessage: editorOutput.spamFilteringPassedMessage,
        },
        { emitEvent: false },
      );

      if (!this.form.valid) {
        throw new Error('All fields are required when spam filtering is enabled.');
      }
    }
    const spamDetectionConfig = spamDetectionConfigEditorOutputToDb(editorOutput);

    await this.conversationConfigService.updateDocFields({
      docId: this.conversationConfig.getId(),
      data: {
        [ConversationConfigSchema.spamDetectionConfig]: spamDetectionConfig,
      },
    });
  };

  ////////////////////////////////////////////////////////////////////////////
  // Validation
  ////////////////////////////////////////////////////////////////////////////

  public isValid = (): boolean => this.form.valid;

  ////////////////////////////////////////////////////////////////////////////
  // Form Helpers
  ////////////////////////////////////////////////////////////////////////////

  public get enabled(): UntypedFormControl {
    return this.form.get('enabled') as UntypedFormControl;
  }

  public get instructions(): UntypedFormControl {
    return this.form.get('instructions') as UntypedFormControl;
  }

  public get spamDetectedMessage(): UntypedFormControl {
    return this.form.get('spamDetectedMessage') as UntypedFormControl;
  }

  public get spamFilteringPassedMessage(): UntypedFormControl {
    return this.form.get('spamFilteringPassedMessage') as UntypedFormControl;
  }

  ////////////////////////////////////////////////////////////////////////////
  // Private
  ////////////////////////////////////////////////////////////////////////////

  private setWaitingRoomEnabled(waitingRoomEnabled: boolean): void {
    this.waitingRoomEnabled = waitingRoomEnabled;
    if (waitingRoomEnabled) {
      this.enabled.disable({ emitEvent: false });
    } else {
      this.enabled.enable({ emitEvent: false });
    }
  }

  private onEnabledChange(): void {
    if (this.enabled.value) {
      this.instructions.setValidators([Validators.required, Validators.minLength(20)]);
      this.spamDetectedMessage.setValidators([Validators.required]);
      this.spamFilteringPassedMessage.setValidators([Validators.required]);
      this.instructions.markAsTouched();
      this.spamDetectedMessage.markAsTouched();
      this.spamFilteringPassedMessage.markAsTouched();
    } else {
      this.instructions.clearValidators();
      this.spamDetectedMessage.clearValidators();
      this.spamFilteringPassedMessage.clearValidators();
    }
    this.instructions.updateValueAndValidity();
    this.spamDetectedMessage.updateValueAndValidity();
    this.spamFilteringPassedMessage.updateValueAndValidity();
  }
}
